import { useCallback, useEffect, useRef } from 'react';

import { useViewAndRateMutation } from '../data/queries';

/**
 * Generates props for a ReactPlayer component which will record its watch time
 *
 * Any additional props needed should be passed to this hook's second argument
 * so that they may be properly merged with the view tracking props.
 *
 * @param video The help mode video resource being played
 * @param props Any additional props that should be applied to the ReactPlayer
 */
const useViewTrackingReactPlayer = (video, props = {}) => {
  // Ref to interact imperatively with the video player
  const reactPlayerRef = useRef(null);

  // Keep track of when the player starts
  const hasVideoStartedRef = useRef(false);
  const handleStart = useCallback(() => {
    hasVideoStartedRef.current = true;
  }, []);

  const { mutate: mutateSecondsLapsed } = useViewAndRateMutation();

  /* Callback that submits the current player elapsed time when invoked */
  const savePlayerProgress = useCallback(
    (reactPlayer, forceSecondsLapsed) => {
      const isForceSecondsLapsed = forceSecondsLapsed != null;

      // If the player hasn't been started and no force time was provided,
      // then saving the player progress is meaningless.
      if (!isForceSecondsLapsed && !hasVideoStartedRef.current) {
        return;
      }

      const secondsLapsed =
        // Use the real video player runtime unless a forced time was used
        isForceSecondsLapsed
          ? forceSecondsLapsed
          : Math.floor(reactPlayer.getCurrentTime());

      mutateSecondsLapsed({
        videoId: video.id,
        secondsLapsed,
      });
    },
    [mutateSecondsLapsed, video.id],
  );

  // Save progress when the modal is opened and closed
  const videoHasBeenViewed = !!video?.views;
  useEffect(() => {
    const reactPlayer = reactPlayerRef.current;
    if (!reactPlayer) {
      return;
    }

    if (!videoHasBeenViewed) {
      savePlayerProgress(reactPlayer, 0);
    }

    return () => {
      if (videoHasBeenViewed) {
        savePlayerProgress(reactPlayer);
      }
    };
  }, [savePlayerProgress, videoHasBeenViewed]);

  // Register an event listener to save progress if the window is closed
  useEffect(() => {
    const handler = () => {
      const reactPlayer = reactPlayerRef.current;
      if (reactPlayer) {
        savePlayerProgress(reactPlayer);
      }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [savePlayerProgress]);

  // Return all of the props that must be applied to the ReactPlayer instance
  // This ensures that the player interacts correctly with the progress tracker
  let url = video.url;
  if (url.includes('youtu') && video.views?.secondsLapsed) {
    url = `${video.url}?t=${video.views.secondsLapsed}`;
  }

  return {
    ...props,
    ref: reactPlayerRef,
    url,
    onStart: handleStart,
  };
};

export default useViewTrackingReactPlayer;
